import {
  describeScopes,
  LinkError,
  type LinkedProduct,
  type LinkStart,
  PRODUCT_NAMES,
  SOCIAL_NAMES,
  SOCIAL_PROVIDERS,
  type SocialProvider,
} from "@rhema/client";
import { Button, TextField } from "@rhema/ui";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ProviderLogo } from "../screens/ProviderButtons";
import { useSession } from "../session/session";
import { rememberPendingLink } from "./pending";
import { useLinkOverview } from "./useLinks";

/**
 * Links an account in another app. Rhema starts the link, shows what it will be able to do, and
 * the person proves the other account with its email and password or with Google, Apple or
 * Microsoft.
 */
export function LinkForm({
  product,
  onDone,
  onCancel,
  wants,
}: {
  product: LinkedProduct;
  onDone?: () => void;
  onCancel?: () => void;
  /** Scopes this link is meant to add. When the other app cannot grant them yet, the form says so. */
  wants?: string[];
}) {
  const { links, reload } = useLinkOverview();
  const { platform, redirect } = useSession();
  const [started, setStarted] = useState<LinkStart | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState(false);
  // Desktop and mobile: the system browser is open on Google, Apple or Microsoft.
  const [inBrowser, setInBrowser] = useState(false);
  const attempt = useRef(0);
  const name = started?.app_name || PRODUCT_NAMES[product];

  const start = useCallback(async () => {
    setError(null);
    setExpired(false);
    setStarted(null);
    try {
      setStarted(await links.api.start(product));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [links, product]);

  useEffect(() => {
    void start();
  }, [start]);

  const failed = (err: unknown) => {
    const code = err instanceof LinkError ? err.code : "failed";
    if (code === "link_expired") setExpired(true);
    setError(
      code === "wrong_password" || code === "invalid"
        ? `That email and password did not work for ${name}.`
        : (err as Error).message,
    );
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!started) return;
    setBusy(true);
    setError(null);
    try {
      await links.api.verifyPassword(product, started.link_token, email.trim(), password);
      setPassword("");
      await links.api.confirm(product, started.link_token);
      await reload();
      onDone?.();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  };

  const social = async (idp: SocialProvider) => {
    if (!started) return;
    if (!platform.linkSocial) {
      rememberPendingLink(product, started.link_token);
      redirect(links.api.socialUrl(product, started.link_token, idp));
      return;
    }
    // The app opens the system browser and finishes the link itself when the browser hands back.
    const mine = ++attempt.current;
    setBusy(true);
    setError(null);
    setInBrowser(true);
    try {
      await platform.linkSocial(product, started.link_token, idp);
      if (mine !== attempt.current) return;
      await reload();
      onDone?.();
    } catch (err) {
      if (mine === attempt.current) failed(err);
    } finally {
      if (mine === attempt.current) {
        setInBrowser(false);
        setBusy(false);
      }
    }
  };

  /** Gives up on a browser that was closed without finishing, and starts a fresh link. */
  const restart = () => {
    attempt.current += 1;
    setInBrowser(false);
    setBusy(false);
    void start();
  };

  if (!started) {
    return (
      <div className="flex flex-col gap-3">
        {error ? (
          <>
            <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
              {error}
            </p>
            <div className="flex gap-2">
              <Button onClick={() => void start()}>Try again</Button>
              {onCancel && (
                <Button variant="ghost" onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </div>
          </>
        ) : (
          <p className="m-0 text-sm text-ink-secondary">Getting ready to link {name}.</p>
        )}
      </div>
    );
  }

  const hasPassword = started.methods.includes("password");
  const providers = SOCIAL_PROVIDERS.filter((p) => started.methods.includes(p));
  const unavailable = wants?.some((s) => !started.scopes.includes(s));

  if (unavailable) {
    return (
      <div className="flex flex-col gap-3">
        <p role="status" className="m-0 text-sm leading-normal text-ink-secondary">
          {name} cannot be given this yet. Your link keeps working as it does now. Try again later.
        </p>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Close
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-sm leading-normal text-ink-secondary">
        {describeScopes(started.scopes, name)} Sign in to your {name} account to allow it.
      </p>

      {providers.length > 0 && (
        <div className="flex flex-col gap-2">
          {providers.map((p) => (
            <Button
              key={p}
              variant="outline"
              disabled={busy}
              icon={<ProviderLogo provider={p} />}
              onClick={() => void social(p)}
            >
              Continue with {SOCIAL_NAMES[p]}
            </Button>
          ))}
        </div>
      )}

      {inBrowser && (
        <p role="status" className="m-0 text-sm text-ink-secondary">
          Finish signing in to {name} in your browser. Rhema completes the link when the browser sends you
          back.{" "}
          <button type="button" className="text-blue underline" onClick={restart}>
            Start again
          </button>
        </p>
      )}

      {hasPassword && (
        <form onSubmit={submit} className="flex flex-col gap-3" aria-label={`Link ${name}`}>
          <TextField
            label={`${name} email`}
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label={`${name} password`}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="m-0 text-xs text-ink-meta">Rhema does not keep your {name} password.</p>
          {error && (
            <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            {expired ? (
              <Button onClick={() => void start()}>Start again</Button>
            ) : (
              <Button type="submit" disabled={busy}>
                {busy ? "Linking" : `Link ${name}`}
              </Button>
            )}
            {onCancel && (
              <Button variant="ghost" onClick={onCancel} disabled={busy}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}

      {!hasPassword && (
        <>
          {error && (
            <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
              {error}
            </p>
          )}
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </>
      )}
    </div>
  );
}
